// First-party aggregate analytics. Never send form values, URLs, queries, or tokens.
export const PUBLIC_PAGES = ['/', '/career', '/career/apply', '/privacy-policy'];
const seenImpressions = new Set();
let lastPage;
let pendingEvents = [];
let flushTimer;

export function flushAnalytics() {
  clearTimeout(flushTimer);
  flushTimer = undefined;
  const events = pendingEvents;
  pendingEvents = [];
  if (!events.length || !analyticsEnabled()) return;
  try {
    const endpoint = process.env.VUE_APP_ANALYTICS_ENDPOINT || 'https://api.mola.games/website-analytics/events';
    Promise.resolve(fetch(endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'omit', keepalive: true, body: JSON.stringify({ events })
    })).catch(() => {});
  } catch { /* Analytics must never interrupt navigation. */ }
}

export function referrerSource(referrer, hostname) {
  if (!referrer) return 'direct';
  try {
    const host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
    if (host === hostname.replace(/^www\./, '')) return 'internal';
    if (/(^|\.)google\.[a-z.]+$/.test(host)) return 'google';
    if (host === 'bing.com' || host.endsWith('.bing.com')) return 'bing';
    if (['instagram.com', 'facebook.com', 'linkedin.com', 't.co', 'x.com', 'tiktok.com'].some(domain => host === domain || host.endsWith(`.${domain}`))) return 'social';
  } catch { /* Only a fixed category is sent. */ }
  return 'other';
}

export function analyticsEnabled() {
  return typeof window !== 'undefined'
    && process.env.NODE_ENV === 'production'
    && ['mola.games', 'www.mola.games'].includes(window.location.hostname)
    && navigator.doNotTrack !== '1' && window.doNotTrack !== '1'
    && navigator.globalPrivacyControl !== true;
}

export function trackEvent(type, details = {}) {
  if (!analyticsEnabled() || document.visibilityState !== 'visible') return;
  const path = window.location.pathname;
  if (!PUBLIC_PAGES.includes(path)) return;
  // Construct an allowlisted payload rather than forwarding arbitrary detail objects.
  try {
    const event = {
      id: window.crypto.randomUUID(), type, path,
      game: details.game || '', store: details.store || '',
      device: window.matchMedia('(max-width: 650px)').matches ? 'mobile' : window.matchMedia('(max-width: 1024px)').matches ? 'tablet' : 'desktop',
      source: referrerSource(document.referrer, window.location.hostname)
    };
    pendingEvents.push(event);
    // A page view and its first impression normally share one small request.
    if (pendingEvents.length >= 8 || type === 'store_click' || type === 'application_submit') flushAnalytics();
    else if (!flushTimer) flushTimer = setTimeout(flushAnalytics, 2000);
  } catch { /* Analytics must never interrupt navigation. */ }
}

export function trackPageView(path) {
  if (!PUBLIC_PAGES.includes(path)) { lastPage = undefined; return; }
  if (!analyticsEnabled() || document.visibilityState !== 'visible' || lastPage === path) return;
  lastPage = path;
  seenImpressions.clear();
  trackEvent('page_view');
}

export function trackGameImpression(game) {
  if (!analyticsEnabled() || document.visibilityState !== 'visible' || seenImpressions.has(game)) return;
  seenImpressions.add(game);
  trackEvent('game_impression', { game });
}

export function installWebsiteAnalytics(router) {
  router.afterEach((to, from, failure) => { if (!failure) trackPageView(to.path); });
  // A page opened in a background tab is counted only when it becomes visible.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') trackPageView(router.currentRoute.value.path);
    else flushAnalytics();
  });
  window.addEventListener('pagehide', flushAnalytics);
}
